export const referenceStates = [
  "resolved",
  "stale",
  "missing",
  "unsupported",
  "refused",
] as const;

export type ReferenceState = (typeof referenceStates)[number];
export type Completeness = "complete" | "partial";

export interface ProjectSummary {
  id: string;
  code: string;
  name: string;
  stage: string;
  role: string;
}

export interface ModelReference {
  id: string;
  externalIdentifier: string;
  name: string;
  discipline: string;
  revision: string;
  source: string;
  referenceState: ReferenceState;
  receivedAt: string;
  unknownFields: Readonly<Record<string, unknown>>;
}

export interface DocumentRecord {
  id: string;
  documentNumber: string;
  title: string;
  revision: string;
  kind: string;
  owner: string;
  modifiedAt: string;
  referenceState: ReferenceState;
  indexCompleteness: Completeness;
  unknownFields: Readonly<Record<string, unknown>>;
}

export interface ElementReference {
  id: string;
  modelReferenceId: string;
  modelRevisionIdentifier: string;
  elementIdentifier: string;
  label: string;
  elementType: string;
  referenceState: ReferenceState;
  locator: Readonly<Record<string, unknown>>;
  unknownFields: Readonly<Record<string, unknown>>;
}

export interface ViewpointReference {
  referenceState: ReferenceState;
  locator: Readonly<Record<string, unknown>>;
  unknownFields: Readonly<Record<string, unknown>>;
}

// A named, standalone viewpoint that belongs to the federation as a whole
// (e.g. a saved coordination view), distinct from IssueRecord.viewpoint
// (which is scoped to one issue). Multiple of these can exist per
// federation. Structurally an identified ViewpointReference.
export interface FederationViewpointRecord extends ViewpointReference {
  id: string;
  label: string;
  createdBy: string;
  createdAt: string;
}

// One BIM-Hub model revision option within a federation, for a model that
// may have more than one positiveRevision on offer at once (e.g. while a
// newer revision is still resolving). Structurally identical in shape to
// src/c1/modelChunks.ts's ModelRevisionTuple (projectId/modelId/
// positiveRevision), duplicated here rather than imported so that
// src/domain/types.ts (foundational, imported by src/c1/*) does not take a
// dependency on src/c1/modelChunks.ts (the reverse direction).
export interface ModelRevisionOption {
  id: string;
  modelReferenceId: string; // FK to ModelReference.id: the model this revision belongs to
  projectId: string;
  modelId: string;
  positiveRevision: number;
  revisionLabel: string; // e.g. "P08" — human-facing, matches ModelReference.revision's convention
  externalIdentifier: string; // opaque upstream revision id, e.g. "mdl:arc:rev-228"
  // Which revision the CDE UI currently presents as "current" for this
  // model, within one modelReferenceId group. This is a CDE UI-local
  // selection (exactly one true per modelReferenceId group is the
  // invariant switchActiveRevision maintains), not a BIM-Hub authority
  // signal — BIM-Hub is not told about, and does not care about, which
  // revision a CDE viewer happens to have selected locally.
  isActive: boolean;
  referenceState: ReferenceState;
  receivedAt: string;
  unknownFields: Readonly<Record<string, unknown>>;
}

export const modelVisibilityStates = ["visible", "hidden", "isolated"] as const;
export type ModelVisibilityState = (typeof modelVisibilityStates)[number];

// A user-local visibility toggle for one ElementReference, as set by a CDE
// UI user in their own session (show/hide/isolate an element in a
// federated view). This is CDE-side local UI state, not a BIM-Hub
// authority: it never round-trips to BIM-Hub, does not affect any other
// collaborator's view, and carries no relationship to the element's
// ReferenceState (an element can be "resolved" and "hidden" at the same
// time — those are two independent axes).
export interface ElementVisibilityState {
  id: string;
  elementReferenceId: string; // FK to ElementReference.id
  visibility: ModelVisibilityState;
  setBy: string;
  setAt: string;
  unknownFields: Readonly<Record<string, unknown>>;
}

export interface IssueRecord {
  id: string;
  externalIdentifier: string;
  title: string;
  topicType: string;
  priority: "critical" | "high" | "normal" | "low";
  assignee: string;
  dueDate: string;
  workflowState: "open" | "in-review" | "closed";
  referenceState: ReferenceState;
  syncCompleteness: Completeness;
  elementReferences: readonly ElementReference[];
  viewpoint?: ViewpointReference;
  unknownFields: Readonly<Record<string, unknown>>;
}

export interface RfiRecord {
  id: string;
  subject: string;
  raisedBy: string;
  owner: string;
  dueDate: string;
  status: "open" | "answered" | "overdue";
  linkedRecords: number;
}

export interface ApprovalRecord {
  id: string;
  subject: string;
  package: string;
  submittedBy: string;
  submittedAt: string;
  status: "awaiting-review" | "changes-requested" | "approved";
  reviewersComplete: number;
  reviewersTotal: number;
}

export interface ActivityRecord {
  id: string;
  verb: string;
  subject: string;
  actor: string;
  occurredAt: string;
}

export interface ScheduleBoundaryFixtureScenario {
  id: string;
  cdeRecordIdentifier: string;
  cdeRecordKind: "Issue" | "RFI" | "Approval" | "Document";
  cdeDueDate: string;
  relationship: "unlinked" | "explicit-candidate";
  candidateControlsReference?: Readonly<Record<string, unknown>>;
  annotation: string;
}

export interface CollaborationFixture {
  fixtureVersion: string;
  generatedAt: string;
  projects: readonly ProjectSummary[];
  activeProjectId: string;
  federation: {
    id: string;
    name: string;
    revisionLabel: string;
    completeness: Completeness;
    references: readonly ModelReference[];
    elements: readonly ElementReference[];
    // C1 additive extensions (see handoff.md "次のgate" #3). Always
    // present as (possibly empty) arrays — an omitted array would be
    // indistinguishable from "not yet loaded"; an empty one is a verified
    // empty set, matching this fixture's known-empty discipline elsewhere.
    revisions: readonly ModelRevisionOption[];
    visibilityStates: readonly ElementVisibilityState[];
    viewpoints: readonly FederationViewpointRecord[];
  };
  documents: readonly DocumentRecord[];
  documentIndexCompleteness: Completeness;
  issues: readonly IssueRecord[];
  issueSyncCompleteness: Completeness;
  scheduleBoundaryScenarios: readonly ScheduleBoundaryFixtureScenario[];
  rfis: readonly RfiRecord[];
  approvals: readonly ApprovalRecord[];
  activity: readonly ActivityRecord[];
}
